import React from "react";

import * as Sentry from "@sentry/browser";
import {produce} from "immer";
import {
    FieldMap,
    FormLike,
    FormSetLike,
    ManagementForm,
    getValue,
    useForm,
    useFormSet,
} from "reactivated/dist/forms";

import {notify} from "@client/features/notifications";

import {Button} from "@client/components/Button";
import {DoubleWidget} from "@client/components/DoubleWidget";
import {Label} from "@client/forms";
import * as widgets from "@client/forms/widgets";
import {widgetStyles as styles} from "@client/forms/widgets";
import {ImperialCaloriesContext, useSiteContext} from "@client/site/context";
import {RPCResult, _Types, classNames, forms} from "@reactivated";

type Result<TSuccess> = RPCResult<TSuccess>;

export {TextInput} from "@client/forms/widgets";
export const CSRFToken = forms.CSRFToken;
export {ManagementForm};

export {useForm, Label, useFormSet, DoubleWidget};

type WidgetType = _Types["globals"]["Widget"];

export type PFieldDefinition<TEnum> =
    | {
          type: "text";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "password";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "email";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "select";
          required: true;
          readOnly?: boolean;
          widget?: "quick" | "select";
          options: readonly (readonly [TEnum, string])[];
          label?: string;
          placeholder?: string;
      }
    | {
          type: "select";
          required?: false;
          readOnly?: boolean;
          widget?: "quick" | "select";
          options: readonly (readonly [TEnum, string])[];
          label?: string;
          placeholder?: string;
      }
    | {
          type: "boolean";
          widget?: "switch" | "checkbox";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "number";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "number";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "decimal";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "decimal";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "date";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "date";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "duration";
          required: true;
          readOnly?: boolean;
          label?: string;
          format?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      }
    | {
          type: "duration";
          required?: false;
          readOnly?: boolean;
          label?: string;
          format?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      }
    | {
          type: "textarea";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "markdown";
          required?: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "length";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "length";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "weight";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "weight";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "energy";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "energy";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "percent";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "percent";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "multiplier";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "multiplier";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "range";
          required: true;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      }
    | {
          type: "range";
          required?: false;
          readOnly?: boolean;
          label?: string;
          placeholder?: string;
      };

export type PFieldHandler<TEnum> =
    | {
          type: "text";
          name: string;
          required?: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "password";
          name: string;
          required?: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "email";
          name: string;
          required?: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "select";
          name: string;
          required: true;
          widget?: "quick" | "select";
          options: readonly (readonly [TEnum, string])[];
          disabledOptions?: TEnum[];
          value: TEnum;
          onChange: (value: TEnum) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "select";
          name: string;
          required?: false;
          widget?: "quick" | "select";
          options: readonly (readonly [TEnum | null, string])[];
          disabledOptions?: (TEnum | null)[];
          value: TEnum | null;
          onChange: (value: TEnum | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "boolean";
          name: string;
          required?: true;
          widget?: "switch" | "checkbox";
          value: boolean;
          onChange: (value: boolean) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "number";
          name: string;
          required: true;
          value: number;
          onChange: (value: number) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "number";
          name: string;
          required?: false;
          value: number | null;
          onChange: (value: number | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "decimal";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "decimal";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "date";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "date";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "duration";
          name: string;
          required: true;
          value: number;
          onChange: (value: number) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
          format?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      }
    | {
          type: "duration";
          name: string;
          required?: false;
          value: number | null;
          onChange: (value: number | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
          format?: "hh:mm:ss" | "hh:mm" | "mm:ss";
      }
    | {
          type: "textarea";
          name: string;
          required?: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "markdown";
          name: string;
          required?: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "length";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "length";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "weight";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "weight";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "energy";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "energy";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "percent";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "percent";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "multiplier";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "multiplier";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "range";
          name: string;
          required: true;
          value: string;
          onChange: (value: string) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      }
    | {
          type: "range";
          name: string;
          required?: false;
          value: string | null;
          onChange: (value: string | null) => void;
          readOnly?: boolean;
          className?: string;
          placeholder?: string;
          label: string;
          errors?: string[] | null;
      };

type ExtractWidget<T extends PFieldDefinition<unknown>> = Extract<
    PFieldHandler<
        T extends {type: "select"; options: infer U}
            ? U extends readonly (readonly [infer TEnum, string])[]
                ? TEnum
                : never
            : never
    >,
    {type: T["type"]; required?: T["required"]}
>;

// Allow null for readOnly fields and nullable types (date, number, decimal, duration, length, weight, percent)
type NullableFieldTypes =
    | "date"
    | "number"
    | "decimal"
    | "duration"
    | "energy"
    | "length"
    | "weight"
    | "percent"
    | "multiplier"
    | "range";
export type TValues<T extends {[K in keyof T]: PFieldDefinition<unknown>}> = {
    [K in keyof T]: T[K]["readOnly"] extends true
        ? ExtractWidget<T[K]>["value"] | null
        : T[K]["type"] extends NullableFieldTypes
          ? ExtractWidget<T[K]>["value"] | null
          : ExtractWidget<T[K]>["value"];
};

type TErrors<T extends {[K in keyof T]: PFieldDefinition<unknown>}> = {
    [K in keyof T]?: string[] | null;
};

export type FieldOverrides = {
    readOnly?: boolean;
    label?: string | null;
    placeholder?: string;
};

type FormOverrides<T extends {[K in keyof T]: PFieldDefinition<unknown>}> = {
    [K in keyof T]?: FieldOverrides;
};

export function usePform<const T extends {[K in keyof T]: PFieldDefinition<unknown>}>(
    fields: T,
    values: TValues<T>,
    setValues: (values: TValues<T>) => void,
    options?: {readOnly?: boolean; overrides?: FormOverrides<T>},
) {
    const [initial] = React.useState(values);
    const [errors, setErrors] = React.useState<TErrors<T> | null>(null);

    const readOnly = options?.readOnly ?? false;
    const overrides = options?.overrides;

    type TFields = {
        [K in keyof T]: ExtractWidget<T[K]>;
    };

    const keys = Object.keys(fields) as Array<keyof T>;
    const fieldsWithHandlers = {} as TFields;

    for (const key of keys) {
        const keyString = key.toString();
        const fieldOverrides = overrides?.[key];
        const defaultLabel = keyString[0].toUpperCase() + keyString.slice(1);
        const overrideLabel = fieldOverrides?.label;
        const overridePlaceholder = fieldOverrides?.placeholder;
        const overrideReadOnly = fieldOverrides?.readOnly;
        /* eslint-disable @typescript-eslint/no-unsafe-assignment */
        fieldsWithHandlers[key] = {
            ...fields[key],
            label: overrideLabel ?? fields[key].label ?? defaultLabel,
            placeholder:
                overridePlaceholder ??
                ("placeholder" in fields[key] ? fields[key].placeholder : ""),
            value: values[key],
            errors: errors?.[key],
            readOnly: overrideReadOnly ?? fields[key].readOnly ?? readOnly,
            name: key,
            onChange: (value: unknown) => {
                const nulledValueIfNecessary =
                    fields[key].type == "select" &&
                    fields[key].required == false &&
                    value == ""
                        ? null
                        : value;

                setValues({...values, [key]: nulledValueIfNecessary});
            },
        } as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        /* eslint-enable @typescript-eslint/no-unsafe-assignment */
    }

    return {
        initial,
        fields: fieldsWithHandlers,
        errors,
        setErrors,
        values,
    };
}

export function useAutoPform<
    const T extends {[K in keyof T]: PFieldDefinition<unknown>},
>(
    schema: {fields: T; defaults: TValues<T>},
    initialValues?: Partial<TValues<T>> | null,
    options?: {readOnly?: boolean; overrides?: FormOverrides<T>},
) {
    const effectiveInitialValues =
        initialValues != null
            ? {...schema.defaults, ...initialValues}
            : schema.defaults;
    const [initialState] = React.useState(effectiveInitialValues);
    const [values, setValues] = React.useState(initialState);

    const pform = usePform(schema.fields, values, setValues, options);

    const reset = React.useCallback(() => {
        setValues(initialState);
    }, [initialState]);

    return {
        ...pform,
        setValues,
        reset,
    };
}

export type PFormLike<T extends {[K in keyof T]: PFieldDefinition<unknown>}> = {
    fields: T;
    defaults: TValues<T>;
    iterator: readonly (keyof T & string)[];
};

export type PFormHandler<
    TSchema extends {
        fields: {[K: string]: PFieldDefinition<unknown>};
        defaults: unknown;
    },
> = ReturnType<typeof useAutoPform<TSchema["fields"]>>;

export async function submitForm<
    TInput,
    TOutput,
    TPrefix extends (keyof TInput & string) | null = null,
>(
    fn: (input: TInput) => Promise<RPCResult<TOutput>>,
    input: NoInfer<TInput>,
    form: {setErrors: (errors: {[key: string]: string[] | null}) => void},
    prefix?: TPrefix,
): Promise<RPCResult<TOutput>> {
    const response = await fn(input);

    if (response.type === "invalid") {
        const errors = response.errors;

        const modelErrors = errors.filter((e) => e.loc.length === 0);
        if (modelErrors.length > 0) {
            const {store} = await import("@client/store");
            modelErrors.forEach((e) => {
                store.dispatch(notify(e.msg, {type: "error"}));
            });
        }

        const errorsMap = Object.fromEntries(
            errors
                .filter((e) =>
                    prefix == null
                        ? e.loc.length === 1
                        : e.loc.length === 2 && e.loc[0] === prefix,
                )
                .map((e) => [prefix == null ? e.loc[0] : e.loc[1], [e.msg]]),
        );
        form.setErrors(errorsMap);
    }

    return response;
}

export function getFieldClass({readOnly}: {readOnly?: boolean}) {
    return classNames(
        "appearance-none text-sm block w-full min-w-0 flex-1 rounded-none rounded-l-md border-0 py-1.5 px-2 sm:px-3 text-gray-900 ring-1 ring-inset ring-stone-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600",
        readOnly == true && "bg-slate-100",
        readOnly != true && "bg-white",
    );
}

export function getSuffixClass({readOnly}: {readOnly?: boolean}) {
    return classNames(
        "text-[11px] sm:text-xs inline-flex items-center rounded-r-md border border-l-0 border-gray-300 px-[0.3rem] sm:px-2 text-gray-900 lowercase",
        readOnly == true && "bg-slate-100",
        // So dragging highlight doesn't show through.
        readOnly != true && "bg-white",
    );
}

export const useUseKilojoules = () => {
    const {profile} = useSiteContext();
    return profile == null ? false : profile.use_kilojoules;
};

export function PWidget<TEnum extends string | boolean | null>(props: {
    field: PFieldHandler<TEnum>;
    className?: string;
    overrides?: FieldOverrides;
}) {
    const field: PFieldHandler<TEnum> = props.overrides
        ? {
              ...props.field,
              readOnly: props.overrides.readOnly ?? props.field.readOnly,
              placeholder: props.overrides.placeholder ?? props.field.placeholder,
              label: props.overrides.label ?? props.field.label,
          }
        : props.field;
    const className = field.className ?? props.className;
    const fieldClass = getFieldClass({readOnly: field.readOnly});
    const suffixClass = getSuffixClass({readOnly: field.readOnly});
    const {profile} = useSiteContext();
    const liveIsImperial = React.useContext(ImperialCaloriesContext).isImperial;
    const isImperial = liveIsImperial ?? (profile == null ? true : profile.is_imperial);
    const useKilojoules = useUseKilojoules();

    if (field.type == "password") {
        return (
            <widgets.PasswordInput
                value={field.value}
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                className={className}
            />
        );
    }

    if (field.type == "decimal") {
        return (
            <widgets.DecimalInput
                value={field.value}
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                placeholder={field.placeholder}
            />
        );
    }

    if (field.type == "email") {
        return (
            <widgets.EmailInput
                value={field.value}
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                className={className}
                placeholder={field.placeholder}
            />
        );
    }

    if (field.type == "boolean") {
        return (
            <widgets.Checkbox
                value={field.value}
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
            />
        );
    }

    if (field.type == "number") {
        return (
            <widgets.DecimalInput
                name={field.name}
                value={field.value?.toString() ?? ""}
                integerOnly
                readOnly={field.readOnly}
                onChange={(value) => {
                    // TODO: handle required number fields. Do we set it to 0? Ignore the change?
                    if (value == null && field.required != true) {
                        field.onChange(null);
                    } else if (value != null) {
                        field.onChange(parseInt(value));
                    }
                }}
                className={classNames(fieldClass, "rounded-r-md")}
                placeholder={field.placeholder}
            />
        );
    }
    if (field.type == "duration") {
        const hours = Math.floor((field.value ?? 0) / 3600);
        const minutes = Math.floor(((field.value ?? 0) % 3600) / 60);
        const seconds = (field.value ?? 0) % 60;
        const setDuration = (value: number) => {
            if (value == 0) {
                // TODO: what happens to required duration inputs?
                if (field.required == true) {
                    field.onChange(30);
                } else {
                    field.onChange(null);
                }
            } else {
                field.onChange(value);
            }
        };

        const updateTime = (
            event: React.ChangeEvent<HTMLInputElement>,
            unit: "hours" | "minutes" | "seconds",
        ) => {
            const {value} = event.target;
            let newValue = parseInt(value);

            if (isNaN(newValue)) {
                newValue = 0;
            }
            if (unit === "hours") {
                setDuration(newValue * 3600 + minutes * 60 + seconds);
            } else if (unit === "minutes") {
                setDuration(hours * 3600 + newValue * 60 + seconds);
            } else {
                setDuration(hours * 3600 + minutes * 60 + newValue);
            }
        };

        return (
            <div
                className="
                    col-span-3 flex gap-1
                    *:flex-1
                "
            >
                <div className="flex">
                    <input
                        type="text"
                        inputMode={"numeric"}
                        className={classNames(fieldClass)}
                        onChange={(e) => updateTime(e, "hours")}
                        placeholder="00"
                        value={hours == 0 ? "" : hours}
                    />
                    <span className={suffixClass}>
                        <span className="sm:hidden">h</span>
                        <span className="max-sm:hidden">hours</span>
                    </span>
                </div>
                <div className="flex">
                    <input
                        type="text"
                        inputMode={"numeric"}
                        className={classNames(fieldClass)}
                        onChange={(e) => updateTime(e, "minutes")}
                        placeholder="00"
                        value={minutes == 0 ? "" : minutes}
                    />
                    <span className={suffixClass}>
                        <span className="sm:hidden">m</span>
                        <span className="max-sm:hidden">minutes</span>
                    </span>
                </div>
                {(field.format == null ||
                    field.format == "hh:mm:ss" ||
                    field.format == "mm:ss") && (
                    <div className="flex">
                        <input
                            type="text"
                            inputMode={"numeric"}
                            className={classNames(fieldClass)}
                            onChange={(e) => updateTime(e, "seconds")}
                            placeholder="00"
                            value={seconds == 0 ? "" : seconds}
                        />
                        <span className={suffixClass}>
                            <span className="sm:hidden">s</span>
                            <span className="max-sm:hidden">seconds</span>
                        </span>
                    </div>
                )}
            </div>
        );
    }
    if (field.type == "date") {
        const onChange =
            field.required == true
                ? (value: string | null) => {
                      if (value == null) {
                          throw new Error("Value is null");
                      }
                      field.onChange(value);
                  }
                : field.onChange;

        return (
            <widgets.DateInput
                value={field.value ?? ""}
                placeholder={field.placeholder}
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={onChange}
            />
        );
    }
    if (field.type == "select") {
        // Convert TEnum to string for HTML widgets
        const toStr = (v: string | boolean | null): string => {
            if (v == null) return "";
            if (typeof v === "boolean") return v ? "true" : "false";
            return v;
        };

        // Convert string back to TEnum by finding in options
        const fromStr = (s: string): TEnum | null => {
            const found = field.options.find(([v]) => toStr(v) === s);
            return found ? found[0] : null;
        };

        const onChange = (value: string | null) => {
            const converted = fromStr(value ?? "");
            if (field.required == true) {
                if (converted == null) {
                    throw new Error("Value is null");
                }
                field.onChange(converted);
            } else {
                field.onChange(converted);
            }
        };

        if (field.widget == null || field.widget == "quick") {
            return (
                <widgets.QuickSelect
                    type="text"
                    name={field.name}
                    options={field.options
                        .filter(
                            ([value]) =>
                                field.disabledOptions == null ||
                                field.disabledOptions.find((d) => d === value) == null,
                        )
                        .map(([value, label]) => ({value: toStr(value), label}))
                        .concat(
                            field.required == true ? [] : [{value: "", label: "None"}],
                        )}
                    readOnly={field.readOnly}
                    onChange={onChange}
                    value={toStr(field.value)}
                    shrinkLast={false}
                />
            );
        } else {
            return (
                <widgets.SelectText
                    value={toStr(field.value)}
                    name={field.name}
                    readOnly={field.readOnly}
                    onChange={onChange}
                    className={className}
                >
                    {field.options.map(([value, label]) => {
                        const strValue = toStr(value);
                        return (
                            <option key={strValue} value={strValue}>
                                {label}
                            </option>
                        );
                    })}
                </widgets.SelectText>
            );
        }
    } else if (field.type == "text") {
        return (
            <widgets.TextInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                className={className}
                placeholder={field.placeholder}
                value={field.value}
            />
        );
    } else if (field.type == "textarea") {
        return (
            <widgets.TextArea
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                className={className}
                placeholder={field.placeholder}
                value={field.value}
            />
        );
    } else if (field.type == "markdown") {
        return (
            <widgets.MarkdownArea
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                className={className}
                placeholder={field.placeholder}
                value={field.value}
            />
        );
    } else if (field.type == "length") {
        return (
            <widgets.LengthInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                isImperial={isImperial}
                onChange={field.onChange}
                value={field.value}
                placeholder={field.placeholder}
            />
        );
    } else if (field.type == "weight") {
        return (
            <widgets.WeightInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                isImperial={isImperial}
                onChange={field.onChange}
                value={field.value}
                className={className}
                placeholder={field.placeholder}
            />
        );
    } else if (field.type == "energy") {
        return (
            <widgets.EnergyInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                useKilojoules={useKilojoules}
                onChange={field.onChange}
                value={field.value}
                placeholder={field.placeholder}
            />
        );
    } else if (field.type == "percent") {
        return (
            <widgets.PercentInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                value={field.value}
                placeholder={field.placeholder}
            />
        );
    } else if (field.type == "multiplier") {
        return (
            <widgets.WeightMultiplierInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                isImperial={isImperial}
                onChange={field.onChange}
                value={field.value}
                placeholder={field.placeholder}
            />
        );
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (field.type == "range") {
        return (
            <widgets.RangeInput
                name={field.name}
                readOnly={field.readOnly ?? false}
                onChange={field.onChange}
                value={field.value}
                placeholder={field.placeholder}
            />
        );
    }
}

export function PField<TEnum extends string | boolean | null>(props: {
    field: PFieldHandler<TEnum>;
    noLabel?: boolean;
    suffix?: string;
    overrides?: FieldOverrides;
}) {
    const label =
        props.overrides?.label !== undefined
            ? props.overrides.label
            : props.noLabel === true
              ? null
              : props.field.label;

    const widget = <PWidget field={props.field} overrides={props.overrides} />;

    return (
        <Label error={props.field.errors?.[0] ?? null} label={label}>
            {props.suffix != null ? (
                <widgets.Suffixed suffix={props.suffix}>{widget}</widgets.Suffixed>
            ) : (
                widget
            )}
        </Label>
    );
}

export const useIsImperial = () => {
    const {profile} = useSiteContext();
    const liveIsImperial = React.useContext(ImperialCaloriesContext).isImperial;

    return liveIsImperial ?? (profile == null ? true : profile.is_imperial);
};
export const useHasHiddenMacros = () => {
    const {profile} = useSiteContext();
    return profile == null ? false : profile.has_hidden_macros;
};

export function handleInputFocusSelectAndPreventContextMenu(event: React.FocusEvent) {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        // Chrome in responsive mode annoyingly shows the context menu
        // So we prevent this.
        target.addEventListener(
            "contextmenu",
            (contextMenuEvent) => {
                contextMenuEvent.preventDefault();
            },
            true, // useCapture
        );
        target.select();
    }
}

export const Widget = ({
    field,
    className,
}: {
    field: forms.FieldHandler;
    className?: string;
}) => {
    const isImperial = useIsImperial();
    const useKilojoules = useUseKilojoules();

    if ("subwidgets" in field) {
        // This has to be an inline style and not use linaria/css for some obscure
        // circular import issue. Try it.
        return (
            <div style={{display: "flex", gap: 10}}>
                {field.subwidgets.map((subwidget) => {
                    return <Widget key={subwidget.name} field={subwidget} />;
                })}
            </div>
        );
    }

    if (field.tag == "django.forms.widgets.HiddenInput") {
        return <input type="hidden" name={field.name} value={field.value ?? ""} />;
    }
    if (
        field.tag == "django.forms.widgets.TextInput" ||
        field.tag == "taggit.forms.TagWidget" ||
        field.tag == "django.forms.widgets.URLInput"
    ) {
        return (
            <widgets.TextInput
                value={field.value ?? ""}
                placeholder={field.widget.attrs.placeholder}
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
            />
        );
    } else if (field.tag == "django.forms.widgets.Textarea") {
        return (
            <widgets.TextArea
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                className={className}
                placeholder={field.widget.attrs.placeholder}
                value={field.value ?? ""}
            />
        );
    } else if (field.tag == "server.core.forms.MarkdownArea") {
        return (
            <widgets.MarkdownArea
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                className={className}
                placeholder={field.widget.attrs.placeholder}
                value={field.value ?? ""}
            />
        );
    } else if (field.tag == "django.forms.widgets.PasswordInput") {
        return (
            <widgets.PasswordInput
                value={field.value ?? ""}
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
            />
        );
    } else if (field.tag == "django.forms.widgets.Select") {
        return (
            <widgets.SelectText
                value={field.value ?? ""}
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                className={className}
            >
                {field.widget.optgroups.map((optgroup) => {
                    const value = (optgroup[1][0].value ?? "").toString();
                    return (
                        <option key={value} value={value}>
                            {optgroup[1][0].label}
                        </option>
                    );
                })}
            </widgets.SelectText>
        );
    } else if (field.tag == "django.forms.widgets.EmailInput") {
        return (
            <widgets.EmailInput
                value={field.value ?? ""}
                placeholder={field.widget.attrs.placeholder}
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
            />
        );
    } else if (
        field.tag == "django.forms.widgets.DateInput" ||
        field.tag == "django.forms.widgets.DateTimeInput"
    ) {
        return (
            <widgets.DateInput
                value={field.value ?? ""}
                placeholder={field.widget.attrs.placeholder}
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
            />
        );
    } else if (field.tag === "django.forms.widgets.CheckboxInput") {
        return (
            <widgets.Switch
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                value={field.value}
            />
        );
    } else if (
        field.tag === "django.forms.widgets.NumberInput" ||
        field.tag == "server.core.forms.IntegerInput"
    ) {
        return (
            <widgets.DecimalInput
                name={field.name}
                integerOnly={field.tag == "server.core.forms.IntegerInput"}
                readOnly={field.disabled}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
                className={className}
            />
        );
    } else if (field.tag === "server.core.forms.DistanceInput") {
        return (
            <widgets.DistanceInput
                name={field.name}
                readOnly={field.disabled}
                isImperial={isImperial}
                onChange={field.handler}
                value={field.value}
                className={className}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.LengthInput") {
        return (
            <widgets.LengthInput
                name={field.name}
                readOnly={field.disabled}
                isImperial={isImperial}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.WeightInput") {
        return (
            <widgets.WeightInput
                name={field.name}
                readOnly={field.disabled}
                isImperial={isImperial}
                onChange={field.handler}
                value={field.value}
                className={className}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.WeightMultiplierInput") {
        return (
            <widgets.WeightMultiplierInput
                name={field.name}
                readOnly={field.disabled}
                isImperial={isImperial}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.EnergyInput") {
        return (
            <widgets.EnergyInput
                name={field.name}
                readOnly={field.disabled}
                useKilojoules={useKilojoules}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.PercentInput") {
        return (
            <widgets.PercentInput
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "server.core.forms.RangeInput") {
        return (
            <widgets.RangeInput
                name={field.name}
                readOnly={field.disabled}
                onChange={field.handler}
                value={field.value}
                placeholder={field.widget.attrs.placeholder}
            />
        );
    } else if (field.tag === "django.forms.widgets.ClearableFileInput") {
        return <input type="file" name={field.name} defaultValue={field.value ?? ""} />;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (field.tag === "server.core.forms.QuickSelect") {
        const options = field.widget.optgroups.map((optgroup) => ({
            label: optgroup[1][0].label,
            value: getValue(optgroup).toString(),
        }));

        return (
            <widgets.QuickSelect
                type="text"
                name={field.name}
                options={options}
                readOnly={field.disabled}
                onChange={field.handler}
                value={field.value}
                shrinkLast={field.widget.attrs.even === true ? "even" : false}
            />
        );
    }

    const exhaustive: never = field;
    throw new Error(`Exhaustive ${JSON.stringify(exhaustive)}`);
};

export const NewField = (props: {
    suffix?: string;
    field: forms.FieldHandler;
    noLabel?: boolean;
}) => {
    const widget = <Widget field={props.field} />;
    if (props.field.tag === "django.forms.widgets.HiddenInput") {
        return widget;
    }

    return (
        <Label
            error={props.field.error}
            label={props.noLabel === true ? null : props.field.label}
        >
            {props.suffix != null ? (
                <widgets.Suffixed suffix={props.suffix}>{widget}</widgets.Suffixed>
            ) : (
                widget
            )}
        </Label>
    );
};

export const Fields = forms.createRenderer<{noLabel?: boolean}>((field, props) => {
    return <NewField field={field} noLabel={props.noLabel} />;
});

interface FormProps<U extends FieldMap> {
    form: FormLike<U>;
    children?: React.ReactNode;
}

export const Form = <U extends FieldMap>(props: FormProps<U>) => {
    return (
        <form noValidate method="POST" action="" encType="multipart/form-data">
            <forms.CSRFToken />

            <Fields form={props.form} />

            {props.children}
            <Button submit>Submit</Button>
        </form>
    );
};

interface FormSetProps<U extends FieldMap> {
    formSet: FormSetLike<U>;
    children?: (props: {
        formSet: FormSetLike<U>;
        addForm: () => void;
    }) => React.ReactNode;
}

export const FormSet = (props: FormSetProps<FieldMap>) => {
    const toFocus = React.useRef<string | null>(null);
    const formSet = useFormSet({
        formSet: props.formSet,
        onAddForm: (form) => {
            toFocus.current = `input[name^=${form.prefix}]:not([type=hidden])`;
        },
    });

    React.useEffect(() => {
        if (toFocus.current != null) {
            document.querySelector<HTMLInputElement>(toFocus.current)?.focus();
            toFocus.current = null;
        }
    }, [toFocus.current]);

    return (
        <>
            <forms.ManagementForm formSet={formSet.schema} />
            <table>
                <thead>
                    <tr>
                        <forms.Iterator form={formSet.schema.empty_form}>
                            {(field) =>
                                field.tag ===
                                "django.forms.widgets.HiddenInput" ? null : (
                                    <th key={field.name}>{field.label}</th>
                                )
                            }
                        </forms.Iterator>
                    </tr>
                </thead>
                <tbody>
                    {formSet.forms.map((handler) => (
                        <tr key={handler.form.prefix}>
                            <forms.Iterator form={handler}>
                                {(field) =>
                                    field.tag ===
                                    "django.forms.widgets.HiddenInput" ? null : (
                                        <td data-name={field.name}>
                                            <Label error={field.error} label={null}>
                                                <Widget field={field} />
                                            </Label>
                                        </td>
                                    )
                                }
                            </forms.Iterator>
                        </tr>
                    ))}
                </tbody>
            </table>
            {props.children?.({addForm: formSet.addForm, formSet: formSet.schema})}
        </>
    );
};

type SingleField<T> = {
    name: T;
    label?: string;
};

type GroupField<T> = {
    label: string;
    fields: [SingleField<T>, SingleField<T>];
};

type Section = {
    section: string;
};

type FieldDeclaration<T> = SingleField<T> | GroupField<T> | Section;

export const useFormLayout = <T extends FieldMap<WidgetType>>(props: {
    form: forms.FormHandler<T>;
    disabled?: true;
    layout: Array<FieldDeclaration<keyof T>>;
}) => {
    const {form, layout} = props;
    let hasErrors = false;

    const fields = layout.map((line, index) => {
        if ("name" in line) {
            const field = produce(form.fields[line.name], (draftState) => {
                draftState.label = line.label ?? draftState.label;
                draftState.disabled = props.disabled ?? false;
            });

            if (form.errors != null && field.name in form.errors) {
                hasErrors = true;
            }

            return <NewField key={field.name} field={field} />;
        } else if ("fields" in line) {
            const lineWidgets = line.fields.map((subLine, index) => {
                const field = produce(form.fields[subLine.name], (draftState) => {
                    draftState.disabled = props.disabled ?? false;
                });

                return (
                    <div key={index} style={{flex: 1}}>
                        <Widget field={field} />
                    </div>
                );
            });
            const lineErrors =
                line.fields
                    .map((subLine) => form.fields[subLine.name].error)
                    .find((error) => error != null) ?? null;

            if (lineErrors != null) {
                hasErrors = true;
            }

            return (
                <Label key={index} label={line.label} error={lineErrors}>
                    <div className={styles.doubleField}>{lineWidgets}</div>
                </Label>
            );
        } else {
            return <p key={index}>{line.section}</p>;
        }
    });
    const component = (
        <div style={{display: "flex", flexDirection: "column", gap: 15}}>{fields}</div>
    );

    return {hasErrors, component};
};

export function usePformLayout<
    T extends {[K in keyof T]: PFieldDefinition<unknown>},
>(props: {
    form: PFormHandler<{fields: T; defaults: TValues<T>}>;
    disabled?: true;
    layout: Array<FieldDeclaration<Extract<keyof T, string>>>;
}) {
    const {form, layout} = props;
    let hasErrors = false;

    const fields = layout.map((line, index) => {
        if ("name" in line) {
            const fieldName = line.name;
            const field = form.fields[fieldName];

            if (form.errors != null && fieldName in form.errors) {
                hasErrors = true;
            }

            return (
                <PField
                    key={fieldName}
                    field={field}
                    overrides={{
                        label: line.label ?? field.label,
                        readOnly: props.disabled ?? field.readOnly,
                    }}
                />
            );
        } else if ("fields" in line) {
            const lineWidgets = line.fields.map((subLine, subIndex) => {
                const fieldName = subLine.name;
                const field = form.fields[fieldName];

                return (
                    <div key={subIndex} style={{flex: 1}}>
                        <PWidget
                            field={field}
                            overrides={{
                                readOnly: props.disabled ?? field.readOnly,
                            }}
                        />
                    </div>
                );
            });

            const lineErrors =
                line.fields
                    .map((subLine) => {
                        const fieldName = subLine.name;
                        return form.fields[fieldName].errors?.[0] ?? null;
                    })
                    .find((error) => error != null) ?? null;

            if (lineErrors != null) {
                hasErrors = true;
            }

            return (
                <Label key={index} label={line.label} error={lineErrors}>
                    <div className={styles.doubleField}>{lineWidgets}</div>
                </Label>
            );
        } else {
            return <p key={index}>{line.section}</p>;
        }
    });

    const component = (
        <div style={{display: "flex", flexDirection: "column", gap: 15}}>{fields}</div>
    );

    return {hasErrors, component};
}

type ExtractRPCFormFromValues<T, TResult extends Result<unknown>> = {
    values: T;
    setErrors:
        | ((errors: Extract<TResult, {type: "invalid"}>["errors"]) => void)
        | "DISABLED";
};

type ExtractRPCParams<T, TResult extends Result<unknown>> = T extends [infer A, infer B]
    ? [A, ExtractRPCFormFromValues<B, TResult>]
    : T extends [infer B]
      ? [ExtractRPCFormFromValues<B, TResult>]
      : never;

// TODO: this could check if there are errors for fields *not* in the form, and also log that. Invisible errors that are quietly not shown.
export function submit<T extends unknown[], TResult extends Result<unknown>>(
    f: (...args: T) => Promise<TResult>,
    ...args: ExtractRPCParams<T, TResult>
) {
    type TSuccess = Extract<TResult, {type: "success"}>["data"];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const form = args[args.length - 1] as forms.FormHandler<any>;
    const possibleParams = args.slice(0, args.length - 1);

    const allArgs = [...possibleParams, form.values];

    const onSuccess =
        (callback: (result: TSuccess) => unknown, invalidCallback?: () => void) =>
        async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const awaited: TResult = await f(...(allArgs as any));

            const {store} = await import("@client/store");
            const {notify} = await import("@client/features/notifications");

            if (awaited.type === "success") {
                callback(awaited.data);
            } else if (awaited.type == "invalid") {
                const errors = awaited.errors as {__all__?: string[]} | null;

                if (errors != null && "__all__" in errors) {
                    errors.__all__?.map((error) => {
                        store.dispatch(notify(error, {type: "error"}));
                    });
                }

                form.setErrors(errors);

                Sentry.withScope((scope) => {
                    scope.setLevel("info");
                    scope.setContext("values", form.values);
                    scope.setContext("errors", errors);
                    Sentry.captureMessage("[Client]: Bad request");
                });
                invalidCallback?.();
            } else {
                store.dispatch(
                    // TODO: based on how many times this happens,
                    // let's expose the request ID to the client
                    // and make the notification permanent till
                    // dismissal.
                    notify(
                        `A server error occurred.
                We have logged it and will look into it. Please try again later.`,
                        {type: "error"},
                    ),
                );

                Sentry.withScope((scope) => {
                    scope.setLevel("error");
                    scope.setContext("values", form.values);
                    scope.setContext("response", awaited);
                    Sentry.captureMessage("[Client]: RPC Error");
                });
            }
        };

    return {
        onSuccess,
    };
}
